/**
 * Editor commands and the FlowScript keymap.
 */
import { EditorSelection } from '@codemirror/state';
import type { EditorView, KeyBinding } from '@codemirror/view';
import { toggleComment } from '@codemirror/commands';
import { formatDocument } from '../language/formatter/format';

/**
 * Reformats the whole document.
 *
 * Offsets are meaningless across a reformat, so the cursor is mapped by *line*:
 * the caret stays on the statement it was on, at the first non-blank column.
 * That is the behaviour people actually expect, and it survives the indentation
 * changing underneath them.
 */
export function formatDocumentCommand(view: EditorView): boolean {
  const current = view.state.doc.toString();
  const result = formatDocument(current);

  // A document with syntax errors is returned untouched — reprinting a tree
  // that error recovery patched up could drop what the user is mid-way through
  // typing.
  if (!result.formatted || result.text === current) return false;

  const head = view.state.selection.main.head;
  const line = view.state.doc.lineAt(head).number;

  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: result.text },
    // `annotations` is deliberately omitted: this is a normal, undoable edit.
  });

  const doc = view.state.doc;
  const targetLine = doc.line(Math.min(Math.max(1, line), doc.lines));
  const column = /^\s*/.exec(targetLine.text)?.[0].length ?? 0;
  view.dispatch({
    selection: EditorSelection.cursor(targetLine.from + column),
    scrollIntoView: true,
  });

  return true;
}

export const flowScriptKeymap: readonly KeyBinding[] = [
  { key: 'Shift-Alt-f', run: formatDocumentCommand, preventDefault: true },
  { key: 'Shift-Mod-f', run: formatDocumentCommand, preventDefault: true },
  { key: 'Mod-/', run: toggleComment, preventDefault: true },
];
